import { apiRequest } from '../../../shared/services/apiClient';
import type { AiPromptUseCaseId } from '../../admin/types/AiPromptConfig';
import type { AiHookActionType, AiHookDefinition, AiHookEvent, UpsertAiHookRequest } from '../types/Hook';

const asEvent = (value: unknown): AiHookEvent => (
  value === 'afterToolUse' || value === 'onHumanHandoff' ? value : 'beforeReply'
);

const asAction = (value: unknown): AiHookActionType => (value === 'prompt' ? 'prompt' : 'http');

const normalizeHook = (value: unknown): AiHookDefinition => {
  const row = (value ?? {}) as Record<string, unknown>;
  return {
    hookId: String(row.hookId ?? ''),
    name: String(row.name ?? ''),
    event: asEvent(row.event),
    actionType: asAction(row.actionType),
    url: row.url ? String(row.url) : null,
    secret: row.secret ? String(row.secret) : null,
    timeoutSeconds: Number(row.timeoutSeconds ?? 3) || 3,
    promptSnippet: row.promptSnippet ? String(row.promptSnippet) : null,
    failOpen: row.failOpen !== false,
    isPublished: Boolean(row.isPublished),
    assignedUseCaseIds: Array.isArray(row.assignedUseCaseIds)
      ? row.assignedUseCaseIds.map(String) as AiPromptUseCaseId[]
      : [],
    toolMatcher: row.toolMatcher ? String(row.toolMatcher) : null,
  };
};

export const listAiHooks = async (): Promise<AiHookDefinition[]> => {
  const result = await apiRequest<unknown[]>('/api/v1/ai/hooks', { method: 'GET' });
  return Array.isArray(result) ? result.map(normalizeHook) : [];
};

export const createAiHook = async (request: UpsertAiHookRequest): Promise<AiHookDefinition> => {
  const result = await apiRequest<unknown>('/api/v1/ai/hooks', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  return normalizeHook(result);
};

export const updateAiHook = async (
  hookId: string,
  request: UpsertAiHookRequest,
): Promise<AiHookDefinition> => {
  const result = await apiRequest<unknown>(
    `/api/v1/ai/hooks/${encodeURIComponent(hookId)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    },
  );
  return normalizeHook(result);
};

export const deleteAiHook = async (hookId: string): Promise<void> => {
  await apiRequest<void>(`/api/v1/ai/hooks/${encodeURIComponent(hookId)}`, { method: 'DELETE' });
};
